import React from "react";
import { format } from "date-fns";
import { Clock, Flame, History, X } from "lucide-react";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { ProgressContext } from "../../../providers/Progress";
import { SPORT_LABELS } from "../../../lib/constants";

function Metric({ icon: Icon, label, value, tone = "default" }) {
    const toneMap = {
        default: "bg-slate-800/60 border-slate-700",
        highlight: "bg-emerald-900/50 border-emerald-700/50",
    };
    return (
        <div
            className={`flex items-center gap-3 p-3 rounded-lg border ${toneMap[tone]}`}
        >
            <div className="p-2 rounded-md bg-slate-900/50">
                <Icon size={16} className="text-slate-300" />
            </div>
            <div className="flex flex-col">
                <span className="text-xs uppercase tracking-[0.1em] text-slate-400">
                    {label}
                </span>
                <span className="text-lg font-semibold text-white">{value}</span>
            </div>
        </div>
    );
}

function StatsModal({ open, onClose }) {
    const { stats, history } = React.useContext(ProgressContext);
    const recent = history.slice(0, 10);

    return (
        <AlertDialog open={open} onOpenChange={onClose}>
            <AlertDialogContent
                handleMouseDownOnOverlay={onClose}
                onEscapeKeyDown={onClose}
                className="max-w-lg bg-slate-900"
            >
                <AlertDialogHeader>
                    <div className="flex items-center justify-between">
                        <AlertDialogTitle className="text-2xl">Your Stats</AlertDialogTitle>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                        >
                            <X size={20} className="text-slate-400" />
                        </button>
                    </div>
                </AlertDialogHeader>
                <div className="space-y-4 mt-4">
                    {/* Sport-specific Stats */}
                    <div className="grid grid-cols-3 gap-3">
                        {["nfl", "nba", "mlb"].map((sport) => {
                            const streak = stats.streaks?.[sport] ?? 0;
                            const best = stats.bestStreaks?.[sport] ?? 0;
                            const sportStats = stats.bySport?.[sport] ?? {};
                            return (
                                <div
                                    key={sport}
                                    className={`flex flex-col p-3 rounded-lg border ${
                                        streak > 0
                                            ? "bg-emerald-900/50 border-emerald-700/50"
                                            : "bg-slate-800/60 border-slate-700"
                                    }`}
                                >
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-300 mb-2 text-center">
                                        {SPORT_LABELS[sport]}
                                    </span>

                                    {/* Streak */}
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                        <Flame size={14} className="text-orange-400" />
                                        <span className="text-xl font-bold text-white">
                                            {streak}
                                        </span>
                                    </div>
                                    <span className="text-[10px] text-slate-500 text-center mb-2">
                                        Best: {best}
                                    </span>

                                    {/* Divider */}
                                    <div className="border-t border-slate-700/50 my-1" />

                                    {/* Win Rate & Games */}
                                    <div className="flex flex-col gap-1 mt-1">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-slate-400">Win%</span>
                                            <span className="text-white font-medium">
                                                {sportStats.winRate ?? 0}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            <span className="text-slate-400">Played</span>
                                            <span className="text-white font-medium">
                                                {sportStats.total ?? 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Overall Totals */}
                    <div className="grid grid-cols-2 gap-3">
                        <Metric
                            icon={Clock}
                            label="Overall win rate"
                            value={`${stats.winRate ?? 0}%`}
                        />
                        <Metric
                            icon={History}
                            label="Total games"
                            value={`${stats.total ?? 0}`}
                        />
                    </div>

                    {/* Recent Games History */}
                    <div className="pt-4 border-t border-slate-800">
                        <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wide">
                            Recent Games
                        </h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {recent.map((entry) => (
                                <div
                                    key={entry.puzzleId}
                                    className="flex items-center justify-between bg-slate-800/60 px-3 py-2 rounded-lg"
                                >
                                    <div>
                                        <p className="text-sm text-white font-semibold">
                                            {SPORT_LABELS[entry.sport] ?? entry.sport?.toUpperCase()}
                                        </p>
                                        <p className="text-xs text-slate-300">
                                            {format(new Date(entry.puzzleDate), "MMM d")} •{" "}
                                            {entry.result === "win" ? "Win" : "Loss"}
                                        </p>
                                    </div>
                                    <span
                                        className={`text-xs px-2 py-1 rounded-full ${
                                            entry.result === "win"
                                                ? "bg-emerald-700/60 text-emerald-100"
                                                : "bg-rose-700/60 text-rose-100"
                                        }`}
                                    >
                                        {entry.guesses ?? 0} guesses
                                    </span>
                                </div>
                            ))}
                            {recent.length === 0 && (
                                <p className="text-sm text-slate-300 text-center py-4">
                                    Play a puzzle to start tracking your progress.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export default StatsModal;
